<template>
  <div class="min-h-screen bg-[#FAFAFA] pb-24">
    <!-- Header -->
    <div class="sticky top-0 z-10 bg-[#FAFAFA] px-4 pt-4 pb-3">
      <div class="flex items-center gap-3">
        <button
          class="text-gray-800 text-lg"
          aria-label="Back"
          @click="router.back()"
        >
          <i class="fas fa-arrow-left" />
        </button>
        <h1 class="text-lg font-normal text-gray-900">
          Archived
        </h1>
      </div>
    </div>

    <section class="px-4 mt-4">
      <p class="text-sm font-normal text-gray-900 mb-2">
        Archived Quiz
      </p>

      <div class="space-y-3">
        <div
          v-for="quiz in archivedQuizzes"
          :key="quiz.guid || quiz.id"
          class="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 flex items-start gap-3 relative"
        >
          <router-link
            :to="`/quiz/${quiz.guid || quiz.id}`"
            class="flex items-start gap-3 flex-1 min-w-0"
          >
            <img
              :src="quiz.image || require('@/assets/quiz.png')"
              alt="thumb"
              class="w-16 h-16 rounded-xl object-cover"
              loading="lazy"
            >
            <div class="flex-1 min-w-0">
              <h3 class="text-[15px] font-semibold text-gray-900 truncate">
                {{ quiz.title }}
              </h3>
              <p class="text-xs text-gray-500 mt-0.5 mb-2 line-clamp-2">
                {{ quiz.description || subtext }}
              </p>
              <div class="grid grid-cols-2 gap-y-1 text-[12px] text-gray-800">
                <div class="flex items-center gap-1">
                  <i class="fas fa-book text-gray-400" />{{ quiz.totalQuiz }} Modul
                </div>
                <div class="flex items-center gap-1 justify-end">
                  <i class="fas fa-clock text-gray-400" />{{ quiz.totalHour }}m
                </div>
                <div class="flex items-center gap-1 col-span-2">
                  <i class="fas fa-user text-gray-400" />{{ quiz.submittedCount || 0 }} Student
                </div>
              </div>
            </div>
          </router-link>

          <button
            class="absolute right-3 top-3 p-1 text-gray-500 hover:text-blue-600 disabled:opacity-50"
            :disabled="archivingId === (quiz.guid || quiz.id)"
            @click.stop="restoreQuiz(quiz)"
          >
            <i
              v-if="archivingId !== (quiz.guid || quiz.id)"
              class="fas fa-box-open text-lg"
            />
            <i
              v-else
              class="fas fa-spinner fa-spin text-lg"
            />
          </button>
        </div>

        <div
          v-if="loading"
          class="text-center text-gray-500 py-3"
        >
          Loading…
        </div>
        <div
          v-if="!loading && !archivedQuizzes.length"
          class="text-center text-gray-400 py-8"
        >
          No archived quizzes
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { GetArchivedQuizzes, ArchiveQuizToggle } from "@/api/quizApi";

const router = useRouter();
const auth = useAuthStore();

const page = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const loading = ref(false);
const archivedQuizzes = ref([]);
const archivingId = ref(null);

const subtext = "Archived quiz";

const normalize = (item) => ({
  id: item?.id ?? item?.guid,
  guid: item?.guid ?? item?.id,
  title: item?.title ?? item?.QuizTitle ?? item?.name ?? "Quiz",
  image: item?.image ?? item?.imagePath ?? item?.thumbnail,
  totalQuiz: item?.totalQuiz ?? item?.TotalQuiz ?? 0,
  totalHour: item?.totalHour ?? item?.TotalHour ?? "",
  submittedCount: item?.submittedCount ?? item?.SubmittedCount ?? 0,
  description: item?.description ?? item?.Description ?? "",
});

const fetchArchived = async () => {
  if (loading.value || page.value > totalPages.value) return;
  loading.value = true;
  try {
    const response = await GetArchivedQuizzes({
      userId: auth.userId,
      page: page.value,
      pageSize: pageSize.value,
    });
    const list = Array.isArray(response?.data) ? response.data : response;
    archivedQuizzes.value.push(...list.map(normalize));
    totalPages.value = response?.totalPages ?? 1;
    page.value++;
  } catch (e) {
    console.error("GetArchivedQuizzes failed:", e?.message || e);
  } finally {
    loading.value = false;
  }
};

const handleScroll = () => {
  const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
  if (scrollTop + clientHeight >= scrollHeight - 50) {
    fetchArchived();
  }
};

const restoreQuiz = async (quiz) => {
  const identifier = quiz.guid || quiz.id;
  if (!identifier) return;
  archivingId.value = identifier;
  const idx = archivedQuizzes.value.findIndex(
    (q) => (q.guid || q.id) === identifier
  );
  const backup = idx !== -1 ? archivedQuizzes.value[idx] : null;
  if (idx !== -1) archivedQuizzes.value.splice(idx, 1);
  try {
    await ArchiveQuizToggle({
      guid: identifier,
      isArchived: false,
      userId: auth.userId,
    });
  } catch (e) {
    if (idx !== -1 && backup) {
      archivedQuizzes.value.splice(idx, 0, backup);
    }
    console.error("Restore failed:", e?.message || e);
  } finally {
    archivingId.value = null;
  }
};

onMounted(() => {
  if (!auth?.isLoggedIn || !auth?.userId) {
    router.replace("/login");
    return;
  }
  fetchArchived();
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
